import {
  LocalStage,
  LocalReasonSolution,
  EditingReasonSolution,
} from "../types";

export const useReasonSolutionManagement = (
  stages: LocalStage[],
  reasonSolutions: LocalReasonSolution[],
  setReasonSolutions: React.Dispatch<
    React.SetStateAction<LocalReasonSolution[]>
  >,
  editingReasonSolution: EditingReasonSolution,
  setEditingReasonSolution: React.Dispatch<
    React.SetStateAction<EditingReasonSolution>
  >,
  setReasonSolutionError: React.Dispatch<React.SetStateAction<string>>,
  getReasonSolutionsForStage: (stageId: string) => LocalReasonSolution[]
) => {
  // Add reason solution click handler
  const handleAddReasonSolutionClick = (stageId: string) => {
    const existingReasonSolutions = getReasonSolutionsForStage(stageId);
    // Find the next available row number
    const existingRows = existingReasonSolutions
      .map((rs) => rs.row)
      .sort((a, b) => a - b);
    let nextRow = 1;
    for (const row of existingRows) {
      if (row === nextRow) {
        nextRow++;
      } else {
        break;
      }
    }

    setEditingReasonSolution({
      stageId,
      reasonSolutionId: null,
      row: nextRow,
      isPrimary: false,
      reason: "",
      solution: "",
      previousReasonIdsJson: "",
    });
    setReasonSolutionError("");
  };

  // Save reason solution handler
  const handleSaveReasonSolution = () => {
    if (
      editingReasonSolution.reason.trim() &&
      editingReasonSolution.solution.trim() &&
      editingReasonSolution.stageId
    ) {
      // Check for duplicate row number within the same stage
      const existingReasonSolutionsInStage = getReasonSolutionsForStage(
        editingReasonSolution.stageId
      );
      const isDuplicateRow = existingReasonSolutionsInStage.some(
        (rs) =>
          rs.row === editingReasonSolution.row &&
          rs.id !== editingReasonSolution.reasonSolutionId
      );

      if (isDuplicateRow) {
        setReasonSolutionError(
          `ردیف ${editingReasonSolution.row} قبلاً در این مرحله استفاده شده است`
        );
        return;
      }

      if (editingReasonSolution.reasonSolutionId) {
        // Update existing reason solution
        const stage = stages.find(
          (s) => s.id === editingReasonSolution.stageId
        );
        setReasonSolutions((prev) => {
          const updated = prev.map((rs) =>
            rs.id === editingReasonSolution.reasonSolutionId
              ? {
                  ...rs,
                  stageRef: stage
                    ? `${stage.sectionId}_${stage.stageNumber}`
                    : "0_0",
                  row: editingReasonSolution.row,
                  isPrimary: editingReasonSolution.isPrimary,
                  reason: editingReasonSolution.reason.trim(),
                  solution: editingReasonSolution.solution.trim(),
                  previousReasonIdsJson:
                    editingReasonSolution.previousReasonIdsJson,
                }
              : rs
          );
          return updated.sort((a, b) => {
            if (a.stageId !== b.stageId)
              return a.stageId.localeCompare(b.stageId);
            return a.row - b.row;
          });
        });
      } else {
        // Add new reason solution
        const stage = stages.find(
          (s) => s.id === editingReasonSolution.stageId
        );
        const reasonSolution: LocalReasonSolution = {
          id: `reason-solution-${Date.now()}`,
          stageId: editingReasonSolution.stageId,
          stageRef: stage ? `${stage.sectionId}_${stage.stageNumber}` : "0_0",
          row: editingReasonSolution.row,
          isPrimary: editingReasonSolution.isPrimary,
          reason: editingReasonSolution.reason.trim(),
          solution: editingReasonSolution.solution.trim(),
          previousReasonIdsJson: editingReasonSolution.previousReasonIdsJson,
        };
        setReasonSolutions((prev) => {
          const updated = [...prev, reasonSolution];
          return updated.sort((a, b) => {
            if (a.stageId !== b.stageId)
              return a.stageId.localeCompare(b.stageId);
            return a.row - b.row;
          });
        });
      }
      setEditingReasonSolution({
        stageId: null,
        reasonSolutionId: null,
        row: 1,
        isPrimary: false,
        reason: "",
        solution: "",
        previousReasonIdsJson: "",
      });
      setReasonSolutionError("");
    }
  };

  // Delete reason solution handler
  const handleDeleteReasonSolution = (reasonSolutionId: string) => {
    setReasonSolutions((prev) =>
      prev.filter((rs) => rs.id !== reasonSolutionId)
    );
  };

  // Edit reason solution handler
  const handleEditReasonSolution = (rs: LocalReasonSolution) => {
    setEditingReasonSolution({
      stageId: rs.stageId,
      reasonSolutionId: rs.id,
      row: rs.row,
      isPrimary: rs.isPrimary,
      reason: rs.reason,
      solution: rs.solution,
      previousReasonIdsJson: rs.previousReasonIdsJson || "",
    });
    setReasonSolutionError("");
  };

  // Cancel editing reason solution
  const handleCancelEditingReasonSolution = () => {
    setEditingReasonSolution({
      stageId: null,
      reasonSolutionId: null,
      row: 1,
      isPrimary: false,
      reason: "",
      solution: "",
      previousReasonIdsJson: "",
    });
    setReasonSolutionError("");
  };

  return {
    handleAddReasonSolutionClick,
    handleSaveReasonSolution,
    handleDeleteReasonSolution,
    handleEditReasonSolution,
    handleCancelEditingReasonSolution,
  };
};
